import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import {
  Button,
  Card,
  Input,
  Modal,
  Space,
  Spin,
  Tabs,
  Typography,
  theme,
} from "antd";
import {
  DownloadOutlined,
  ExpandOutlined,
  FileTextOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { downloadCommercialInvoice } from "../../../utils/commercialInvoiceUtils";
import { useCommercialInvoicePreview } from "../hooks/useCommercialInvoicePreview";
import { useStickySidePanel } from "../hooks/useStickySidePanel";
import type { ExcelPreviewData } from "../../../utils/excelPreviewUtils";

const { Text } = Typography;

/** A4 portrait ratio (210 × 297 mm) */
const A4_ASPECT_RATIO = 210 / 297;

const PlaceholderColumn = styled.div`
  width: min(420px, 40vw);
  min-width: 300px;
  flex-shrink: 0;
  align-self: flex-start;

  @media (max-width: 1100px) {
    width: 100%;
    min-width: 0;
  }
`;

const SidePanel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const PreviewFrame = styled.div`
  width: 100%;
  aspect-ratio: ${A4_ASPECT_RATIO};
  height: auto;
  min-height: 560px;
  max-height: min(calc(100vh - 220px), 820px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  user-select: text;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.02);
  position: relative;
  cursor: zoom-in;

  &:hover {
    border-color: rgba(99, 102, 241, 0.35);
  }
`;

const PreviewClickLayer = styled.button`
  position: absolute;
  inset: 0;
  z-index: 2;
  margin: 0;
  padding: 0;
  border: none;
  cursor: zoom-in;
  background: transparent;
`;

const ExcelPreviewTable = ({ data }: { data: ExcelPreviewData }) => {
  const colCount = data.rows.reduce((max, row) => Math.max(max, row.length), 0);

  return (
    <div style={{ overflow: "auto", background: "#fff", userSelect: "text", height: "100%" }}>
      {data.truncated ? (
        <Text type="secondary" style={{ fontSize: 11, display: "block", padding: 8 }}>
          Showing first {data.rows.length} rows
        </Text>
      ) : null}
      <table
        style={{
          borderCollapse: "collapse",
          fontSize: 11,
          width: "max-content",
          minWidth: "100%",
        }}
      >
        <tbody>
          {data.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: colCount }, (_, colIndex) => (
                <td
                  key={colIndex}
                  style={{
                    border: "1px solid #f0f0f0",
                    padding: "3px 8px",
                    whiteSpace: "nowrap",
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    background: rowIndex === 0 ? "#fafafa" : undefined,
                    fontWeight: rowIndex === 0 ? 600 : undefined,
                  }}
                >
                  {row[colIndex] ?? ""}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface CommercialInvoiceSidePanelProps {
  commercialInvoiceFile: string;
  bookingId?: number;
  extractedText?: string;
}

const DocumentPreview = ({
  loading,
  isExcel,
  excelPreview,
  previewUrl,
  canInlinePreview,
  fileType,
  primaryFileName,
  height,
  interactive = false,
}: {
  loading: boolean;
  isExcel: boolean;
  excelPreview: ExcelPreviewData | null;
  previewUrl: string | null;
  canInlinePreview: boolean;
  fileType: ReturnType<typeof import("../../../utils/containerDocumentsUtils").getFileType>;
  primaryFileName: string;
  height: number | string;
  interactive?: boolean;
}) => {
  if (loading) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin />
      </div>
    );
  }

  if (isExcel && excelPreview) {
    return <ExcelPreviewTable data={excelPreview} />;
  }

  if (previewUrl && canInlinePreview && fileType.type === "pdf") {
    return (
      <iframe
        src={previewUrl}
        style={{
          width: "100%",
          minHeight: "100%",
          height: height === "100%" ? "100%" : height,
          border: "none",
          display: "block",
          pointerEvents: interactive ? "auto" : "none",
        }}
        title={primaryFileName}
      />
    );
  }

  if (previewUrl && fileType.type === "image") {
    return (
      <img
        src={previewUrl}
        alt={primaryFileName}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          background: "#fff",
        }}
      />
    );
  }

  return (
    <div
      style={{
        height,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <Text type="secondary" style={{ textAlign: "center", fontSize: 12 }}>
        {primaryFileName}
      </Text>
    </div>
  );
};

export const CommercialInvoiceSidePanel = ({
  commercialInvoiceFile,
  bookingId,
  extractedText,
}: CommercialInvoiceSidePanelProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [modalOpen, setModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [stickyEnabled, setStickyEnabled] = useState(
    () => typeof window !== "undefined" && window.innerWidth > 1100
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1100px)");
    const sync = () => setStickyEnabled(!media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  const {
    primaryPath,
    primaryFileName,
    fileType,
    isExcel,
    canInlinePreview,
    previewUrl,
    excelPreview,
    loading,
  } = useCommercialInvoicePreview(commercialInvoiceFile, bookingId);

  const { placeholderRef, panelRef, panelStyle } = useStickySidePanel(
    stickyEnabled,
    loading ? "loading" : primaryPath
  );

  if (!primaryPath) return null;

  const handleDownload = async () => {
    if (!primaryPath) return;
    setDownloading(true);
    try {
      await downloadCommercialInvoice(
        primaryPath,
        primaryFileName.replace(/\.[^.]+$/, ""),
        bookingId
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenNewTab = () => {
    if (previewUrl) window.open(previewUrl, "_blank", "noopener,noreferrer");
  };

  const handlePreviewClick = () => {
    if (!loading) setModalOpen(true);
  };

  const documentPreview = (
    <DocumentPreview
      loading={loading}
      isExcel={isExcel}
      excelPreview={excelPreview}
      previewUrl={previewUrl}
      canInlinePreview={canInlinePreview}
      fileType={fileType}
      primaryFileName={primaryFileName}
      height="100%"
    />
  );

  const tabItems = [
    {
      key: "document",
      label: t("bookingWizardV2.invoicePreview.documentTab"),
      children: (
        <PreviewFrame
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handlePreviewClick();
            }
          }}
        >
          {documentPreview}
          {!loading ? (
            <PreviewClickLayer
              type="button"
              aria-label={t("bookingWizardV2.invoicePreview.clickToZoom")}
              title={t("bookingWizardV2.invoicePreview.clickToZoom")}
              onClick={handlePreviewClick}
            />
          ) : null}
        </PreviewFrame>
      ),
    },
    ...(extractedText?.trim()
      ? [
          {
            key: "text",
            label: t("bookingWizardV2.invoicePreview.textTab"),
            children: (
              <Input.TextArea
                value={extractedText}
                readOnly
                autoSize={{ minRows: 22, maxRows: 32 }}
                style={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  userSelect: "text",
                  cursor: "text",
                }}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PlaceholderColumn ref={placeholderRef}>
        <div ref={panelRef} style={panelStyle}>
          <SidePanel>
            <Card
            size="small"
            title={
              <Space size={6}>
                <FileTextOutlined />
                <span>{t("bookingWizardV2.invoicePreview.title")}</span>
              </Space>
            }
            styles={{ body: { padding: 12 } }}
            style={{ borderColor: token.colorBorderSecondary }}
          >
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", marginBottom: 8 }}
            >
              {primaryFileName}
            </Text>
            <Text
              type="secondary"
              style={{ fontSize: 11, display: "block", marginBottom: 8 }}
            >
              {t("bookingWizardV2.invoicePreview.hint")}
            </Text>

            <Tabs
              size="small"
              items={tabItems}
              style={{ width: "100%" }}
              tabBarStyle={{ marginBottom: 8 }}
            />

            <Space wrap style={{ marginTop: 12, width: "100%" }}>
              <Button
                size="small"
                icon={<ExpandOutlined />}
                onClick={() => setModalOpen(true)}
                disabled={loading}
              >
                {t("bookingWizardV2.invoicePreview.expand")}
              </Button>
              {previewUrl && !isExcel && (
                <Button size="small" icon={<ExportOutlined />} onClick={handleOpenNewTab}>
                  {t("bookingWizardV2.invoicePreview.openNewTab")}
                </Button>
              )}
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={handleDownload}
              >
                {t("bookingWizardV2.invoicePreview.download")}
              </Button>
            </Space>
          </Card>
          </SidePanel>
        </div>
      </PlaceholderColumn>

      <Modal
        title={primaryFileName}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width="min(960px, calc(100vw - 32px))"
        styles={{ body: { padding: 0 } }}
        footer={[
          previewUrl && !isExcel ? (
            <Button key="open" icon={<ExportOutlined />} onClick={handleOpenNewTab}>
              {t("bookingWizardV2.invoicePreview.openNewTab")}
            </Button>
          ) : null,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloading}
            onClick={handleDownload}
          >
            {t("bookingWizardV2.invoicePreview.download")}
          </Button>,
          <Button key="close" onClick={() => setModalOpen(false)}>
            {t("common.close", "Close")}
          </Button>,
        ]}
      >
        <div style={{ height: "80vh", overflow: "auto", userSelect: "text" }}>
          <DocumentPreview
            loading={loading}
            isExcel={isExcel}
            excelPreview={excelPreview}
            previewUrl={previewUrl}
            canInlinePreview={canInlinePreview}
            fileType={fileType}
            primaryFileName={primaryFileName}
            height="80vh"
            interactive
          />
        </div>
        {extractedText?.trim() ? (
          <div style={{ marginTop: 16, padding: "0 24px 24px" }}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              {t("bookingWizardV2.invoicePreview.textTab")}
            </Text>
            <Input.TextArea
              value={extractedText}
              readOnly
              autoSize={{ minRows: 6, maxRows: 12 }}
              style={{ fontFamily: "monospace", fontSize: 11, userSelect: "text" }}
            />
          </div>
        ) : null}
      </Modal>
    </>
  );
};
